import { ref as vueRef, computed, watch, getCurrentInstance, onUnmounted } from "vue";
import { getDatabase, ref, onValue, set } from "firebase/database";
import { authStore } from "@/store/auth";
import { flagsStore } from "@/store/countryFlags.js";
import Languages, { languageName } from "@/data/Language.js";
import i18n from "@/locales";
import { useToast } from "@/composables/useToast.js";

// Shared learning-language state for the switcher surfaces (desktop sidebar
// dropdown, mobile tab-bar sheet). Switching is an optimistic direct client
// write of selectedLanguage: RTDB rules are the validator (Fluency may only
// switch among held languages; Free/Foundation are locked), so a rejected
// write reverts the optimistic value and shows a toast.
export function useLearningLanguages() {
  const { showToast } = useToast();
  const userData = vueRef({});
  // Optimistic override so switching feels instant; cleared on confirm/revert.
  const optimisticLanguage = vueRef(null);

  // The listener follows authStore.userId: at mount the uid may not have
  // resolved yet, and it changes again on sign-out/sign-in.
  const db = getDatabase();
  let off = null;
  const stopUserWatch = watch(
    () => authStore.userId,
    (userId) => {
      if (off) {
        off();
        off = null;
      }
      userData.value = {};
      optimisticLanguage.value = null;
      if (!userId) return;
      off = onValue(
        ref(db, `users/${userId}`),
        (snap) => {
          userData.value = snap.val() || {};
          optimisticLanguage.value = null;
        },
        (error) => {
          console.error("Failed to subscribe to user data:", error);
        }
      );
    },
    { immediate: true }
  );
  if (getCurrentInstance()) {
    onUnmounted(() => {
      if (off) off();
      stopUserWatch();
    });
  }

  const languageAlt = (code) => Languages[code]?.altText || "Country flag";

  const displayedLanguage = computed(
    () => optimisticLanguage.value || userData.value.selectedLanguage
  );
  const isMultiLingual = computed(
    () => userData.value.features?.multiLingual === true
  );
  // Mirrors the server's entitlement (helpers/users.isLanguageEntitled):
  // Fluency may use every held language; Free/Foundation — including
  // downgraded ex-Fluency, whose dormant languages stay stored — only the
  // active one. Rules would reject switching to a dormant language anyway;
  // not listing it avoids offering a dead tap.
  const heldLanguages = computed(() => {
    const held = Object.keys(userData.value.languagePreferences || {});
    const entitled = isMultiLingual.value
      ? held
      : held.filter((code) => code === userData.value.selectedLanguage);
    const active = displayedLanguage.value;
    return entitled.sort((a, b) =>
      a === active ? -1 : b === active ? 1 : languageName(a).localeCompare(languageName(b))
    );
  });
  const activeFlagSrc = computed(() =>
    flagsStore.getFlag(displayedLanguage.value || "unknown")
  );
  const activeAltText = computed(() => languageAlt(displayedLanguage.value));

  const switchTo = async (code) => {
    if (!code || code === userData.value.selectedLanguage) return;
    optimisticLanguage.value = code;
    try {
      await set(ref(db, `users/${authStore.userId}/selectedLanguage`), code);
    } catch (error) {
      console.error("Language switch rejected:", error);
      optimisticLanguage.value = null;
      showToast(i18n.global.t("languageSwitcher.switchFailed"), "error");
    }
  };

  return {
    heldLanguages,
    displayedLanguage,
    isMultiLingual,
    activeFlagSrc,
    activeAltText,
    languageName,
    languageAlt,
    switchTo,
  };
}
